import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authenticatedPost, authenticatedGet, authenticatedRequest } from '@/utils/api'
import { useRootStore } from './root'
import { useAuthStore } from './auth'
import { usePerimeterStore } from './perimeter'
import { useSolvencyStore } from './solvency'
import type { SCRComputationPayload } from './solvency'
import { assertPerimeterSelection } from '@/utils/perimeterValidation'
// DÉSACTIVÉ - Modules ESG et Risk
// import { useEsgStore } from './esg'
// import { useRiskStore } from './risk'

interface PositionsCountResponse {
  before: number
  after: number
}

interface TransparisationResponse extends SCRComputationPayload {
  perimeter: {
    perimeterID: string
  }
  globalMarketValuation?: number
}

export const useTransparisationStore = defineStore('transparisation', () => {
  const rootStore = useRootStore()
  const authStore = useAuthStore()
  const perimeterStore = usePerimeterStore()

  // State
  const loading = ref(false)
  const loaded = ref(false)
  const perimeterId = ref<string | undefined>(undefined)
  const transparisationDate = ref<Date | undefined>(undefined)
  const totalMarketValuation = ref<number | null>(null)
  const transpaReportLoading = ref(false)
  const cachedOriginalPositions = ref<unknown[]>([]) // Cache des positions pour restauration après simulation
  const positionsCountBefore = ref<number | null>(null) // Nombre de positions avant transparisation (niveau 0)
  const positionsCountAfter = ref<number | null>(null) // Nombre de positions après transparisation (tous niveaux)

  // Computed
  const inventoryLoaded = computed(() => loaded.value)
  const inventoryLoading = computed(() => loading.value)

  // Actions
  const reset = () => {
    loading.value = false
    loaded.value = false
    perimeterId.value = undefined
    transparisationDate.value = undefined
    totalMarketValuation.value = null
    transpaReportLoading.value = false
    cachedOriginalPositions.value = []
    positionsCountBefore.value = null
    positionsCountAfter.value = null
  }

  /**
   * Récupère la valorisation totale du marché
   */
  const getTotalMarketValuation = async (perimeterIdParam?: string) => {
    try {
      const baseUrl = rootStore.baseUrl
      const pidToUse = perimeterIdParam || perimeterId.value

      if (!pidToUse) {
        console.warn('No perimeter ID available for getTotalMarketValuation')
        return
      }

      const response = await authenticatedGet(baseUrl + '/api/inventory/global-market-valuation/', {
        params: {
          perimeterID: pidToUse,
        },
      })

      totalMarketValuation.value = response.data
    } catch (err) {
      console.error('Erreur récupération valorisation totale:', err)
    }
  }

  /**
   * Récupère le nombre de positions avant et après transparisation
   */
  const getPositionsCount = async (perimeterIdParam?: string) => {
    try {
      const baseUrl = rootStore.baseUrl
      const pidToUse = perimeterIdParam || perimeterId.value

      if (!pidToUse) {
        console.warn('No perimeter ID available for getPositionsCount')
        return
      }

      const response = await authenticatedGet<PositionsCountResponse>(`${baseUrl}/api/inventory/${pidToUse}/positions-count`)

      positionsCountBefore.value = response.data.before
      positionsCountAfter.value = response.data.after
    } catch (err) {
      console.error('Erreur récupération comptage positions:', err)
    }
  }

  /**
   * Lance le calcul et la sauvegarde des positions hiérarchiques (transparisation)
   * Peut aussi lancer les calculs SCR, ESG, Risk en même temps pour éviter
   * de créer plusieurs périmètres
   */
  const computeSaveHierarchicalPositions = async (options?: {
    computeSCR?: boolean
    computeESG?: boolean
    computeRisk?: boolean
  }) => {
    const account = authStore.userAccount
    if (!account) {
      throw new Error('User not authenticated')
    }

    if (loading.value) {
      throw new Error('Un calcul est deja en cours')
    }

    assertPerimeterSelection(
      {
        client: perimeterStore.client,
        portfolios: perimeterStore.portfolios,
        valuationDate: perimeterStore.valuationDate,
        compartments: perimeterStore.compartments,
        lookThroughLevel: perimeterStore.transparencyLevel,
      },
      'Configuration de perimetre incomplete'
    )

    loading.value = true
    loaded.value = false

    try {
      const baseUrl = rootStore.baseUrl
      const userId = account.localAccountId || account.homeAccountId

      // Réinitialiser le mode simulation si on lance une nouvelle transparisation normale
      const simulationStore = await import('./simulation').then(m => m.useSimulationStore())
      if (simulationStore.simulationMode) {
        simulationStore.reset()
      }

      // D'abord sauvegarder le périmètre
      await perimeterStore.savePerimeter()

      // Puis lancer le calcul de transparisation (avec calculs optionnels)
      const response = await authenticatedPost<TransparisationResponse>(baseUrl + '/api/inventory/computation', {
        data: {
          valuationDate: perimeterStore.valuationDate,
          rolename: perimeterStore.client?.code,
          portfolios: perimeterStore.portfolios.map(p => p.code),
          compartments: perimeterStore.compartments.map(c => c.code),
          lookThroughLevel: perimeterStore.transparencyLevel,
          userID: userId,
          computeScr: options?.computeSCR || false,
          computeEsg: options?.computeESG || false,
          computeRiskLiquidity: options?.computeRisk || false,
        },
        timeoutProfile: 'compute',
      })

      // Mettre à jour l'état
      perimeterId.value = response.data.perimeter.perimeterID
      loaded.value = true
      transparisationDate.value = new Date()

      // Utiliser globalMarketValuation de la réponse au lieu de faire un appel API séparé
      if (response.data.globalMarketValuation !== undefined) {
        totalMarketValuation.value = response.data.globalMarketValuation
      } else {
        // Fallback : si la réponse ne contient pas globalMarketValuation (ancien backend?)
        await getTotalMarketValuation(response.data.perimeter.perimeterID)
      }

      // Récupérer le comptage des positions (avant/après)
      await getPositionsCount(response.data.perimeter.perimeterID)

      // Dispatcher les résultats aux stores correspondants si les calculs ont été faits
      if (options?.computeSCR && response.data.globalSCRMarketResult) {
        const solvencyStore = useSolvencyStore()
        solvencyStore.setScrMarketResult(response.data)
        solvencyStore.setScrDone(true)
      }
      
      // DÉSACTIVÉ - Modules ESG et Risk
      // if (options?.computeESG && response.data.globalESGData) {
      //   const esgStore = useEsgStore()
      //   esgStore.setEsgResults(response.data)
      //   esgStore.setEsgDone(true)
      // }

      // Réinitialiser le flag d'obsolescence des résultats
      const { useSimulationStore } = await import('./simulation')
      const simStore = useSimulationStore()
      simStore.setResultsObsolete(false)

      return response.data
    } catch (err) {
      console.error('Erreur calcul transparisation:', err)
      perimeterId.value = undefined
      loaded.value = false
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Lance un calcul (SCR, ESG, Risk)
   */
  const compute = async (options: {
    computeSCR?: boolean
    computeESG?: boolean
    computeRisk?: boolean
  }) => {
    const account = authStore.userAccount
    if (!account) {
      throw new Error('User not authenticated')
    }

    if (loading.value) {
      throw new Error('Un calcul est deja en cours')
    }

    assertPerimeterSelection(
      {
        client: perimeterStore.client,
        portfolios: perimeterStore.portfolios,
        valuationDate: perimeterStore.valuationDate,
        compartments: perimeterStore.compartments,
        lookThroughLevel: perimeterStore.transparencyLevel,
      },
      'Configuration de perimetre incomplete'
    )

    try {
      const baseUrl = rootStore.baseUrl
      const userId = account.localAccountId || account.homeAccountId
      const response = await authenticatedPost<TransparisationResponse>(baseUrl + '/api/inventory/computation', {
        data: {
          valuationDate: perimeterStore.valuationDate,
          rolename: perimeterStore.client?.code,
          portfolios: perimeterStore.portfolios.map(p => p.code),
          compartments: perimeterStore.compartments.map(c => c.code),
          lookThroughLevel: perimeterStore.transparencyLevel,
          userID: userId,
          computeEsg: options.computeESG || false,
          computeScr: options.computeSCR || false,
          computeRiskLiquidity: options.computeRisk || false,
        },
        timeoutProfile: 'compute',
      })

      perimeterId.value = response.data.perimeter.perimeterID

      // Dispatcher les résultats aux stores correspondants
      if (options.computeSCR) {
        const solvencyStore = useSolvencyStore()
        solvencyStore.setScrMarketResult(response.data)
        solvencyStore.setScrDone(true)
      }
      
      // DÉSACTIVÉ - Modules ESG et Risk
      // if (options.computeESG) {
      //   const esgStore = useEsgStore()
      //   esgStore.setEsgResults(response.data)
      //   esgStore.setEsgDone(true)
      // }
      
      // if (options.computeRisk) {
      //   const riskStore = useRiskStore()
      //   // TODO: riskStore.setResults(response.data)
      // }

      // Réinitialiser le flag d'obsolescence des résultats après un calcul
      const { useSimulationStore } = await import('./simulation')
      const simStore = useSimulationStore()
      simStore.setResultsObsolete(false)

      return response.data
    } catch (err) {
      console.error('Erreur calcul:', err)
      throw err
    }
  }

  /**
   * Exporte le rapport de transparisation en Excel
   */
  const exportTranspaExcel = async () => {
    const account = authStore.userAccount
    if (!account) {
      throw new Error('User not authenticated')
    }

    transpaReportLoading.value = true

    try {
      const baseUrl = rootStore.baseUrl
      const userId = account.localAccountId || account.homeAccountId

      const response = await authenticatedRequest({
        url: baseUrl + '/api/reporting/lookthrough',
        method: 'GET',
        params: {
          userID: userId,
        },
        responseType: 'blob',
        timeoutProfile: 'download',
      })

      // Extraire le nom de fichier du header content-disposition
      const contentDisposition = response.headers['content-disposition']
      let filename = 'transparisation.xlsx'
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '').trim()
        }
      }

      // Télécharger le fichier
      const blob = new Blob([response.data])
      const link = document.createElement('a')
      link.href = window.URL.createObjectURL(blob)
      link.download = filename
      link.click()
      window.URL.revokeObjectURL(link.href)
    } catch (err) {
      console.error('Erreur export Excel transparisation:', err)
      throw err
    } finally {
      transpaReportLoading.value = false
    }
  }

  return {
    // State
    loading,
    loaded,
    perimeterId,
    transparisationDate,
    totalMarketValuation,
    transpaReportLoading,
    cachedOriginalPositions,
    positionsCountBefore,
    positionsCountAfter,

    // Computed
    inventoryLoaded,
    inventoryLoading,

    // Actions
    reset,
    getTotalMarketValuation,
    getPositionsCount,
    computeSaveHierarchicalPositions,
    compute,
    exportTranspaExcel,
  }
}, {
  persist: {
    key: 'arago-transparisation',
    storage: sessionStorage, // sessionStorage pour sécurité (montants financiers)
    pick: ['loaded', 'perimeterId', 'transparisationDate', 'totalMarketValuation', 'positionsCountBefore', 'positionsCountAfter'],
  },
})
